import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import styles from "./styles/Navbar.module.css";
import { ArrowRightIcon, ChevronDownIcon } from "@heroicons/react/16/solid";
import { IconMenuTable, IconMenuTableSolid } from "./assets/icons";
import { useAuth } from "./context/authContext";

export const Navbar = ({ scrollToSection, botRef, joinRef, learnRef }) => {
  const { user } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false); // Desktop
  const [isMobileProductOpen, setIsMobileProductOpen] = useState(true); // Mobile

  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const productPaths = {
    xenoarc: "/learn-more/xenoarc",
    grid: "/learn-more/grid",
    dca: "/learn-more/dca",
  };

  const isActive = (path) => {
    if (path === location.pathname) return true;

    // Group check for all product links
    if (path === "product-group") {
      return Object.values(productPaths).some((p) =>
        location.pathname.startsWith(p),
      );
    }
    return false;
  };

  // helper for mobile About Product active state
  const isProductGroupActive = Object.values(productPaths).some((p) =>
    location.pathname.startsWith(p),
  );

  // Close desktop dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProductDropdownOpen(false);
      }
    };
    if (isProductDropdownOpen)
      document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isProductDropdownOpen]);

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "unset";
  }, [isMenuOpen]);

  const handleMobileLinkClick = (path) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  // Handle mouse enter for desktop hover
  const handleMouseEnter = () => {
    setIsProductDropdownOpen(true);
  };

  // Handle mouse leave for desktop hover
  const handleMouseLeave = () => {
    setIsProductDropdownOpen(false);
  };

  return (
    <nav className={styles.navbar}>
      {/* 1. LOGO & HAMBURGER */}
      <div className={styles.logoContainer}>
        <button
          className="lg:hidden !bg-[#F3F4F7] !p-2 rounded-md mr-4 shadow-[0px_1px_4px#0C0C0D10,0px_1px_4px#0C0C0D10]"
          onClick={() => setIsMenuOpen((prev) => !prev)}
        >
          {!isMenuOpen ? (
            <IconMenuTable className="w-[22px] h-[22px] text-[#4A3880]" />
          ) : (
            <IconMenuTableSolid className="w-[22px] h-[22px] text-[#4A3880]" />
          )}
        </button>

        <div
          onClick={() => {
            navigate("/");
            setIsMenuOpen(false);
          }}
          className="cursor-pointer"
        >
          <img
            src="/images/icons/icon_xenorize.svg"
            alt="Platform logo"
            className={styles.logoIcon}
          />
        </div>
      </div>

      {/* 2. DESKTOP MENU */}
      <div className={`${styles.menuContainer} ${styles.desktopOnly}`}>
        {/* Product Dropdown */}
        <div
          className="relative"
          ref={dropdownRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <button
            className={`${styles.menuItem} ${
              isActive("product-group") ? styles.menuItemActive : ""
            }`}
            onClick={() => setIsProductDropdownOpen(!isProductDropdownOpen)}
          >
            Automated Strategy
            <ChevronDownIcon
              className={`w-4 h-4 transition-transform lg:hidden ${
                isProductDropdownOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {isProductDropdownOpen && (
            <>
              {/* Invisible bridge to span the gap */}
              <div className="hidden lg:block absolute top-full left-0 w-full h-2" />

              <div className="lg:absolute relative top-0 lg:top-[calc(100%)] left-0 lg:mt-2 bg-white lg:rounded-lg lg:shadow-lg p-3 lg:min-w-[200px] z-50 flex flex-col gap-2 w-full lg:w-auto">
                {/* Xenoarc */}
                <Link
                  to={productPaths.xenoarc}
                  className={`w-full text-left text-sm px-3 py-2 rounded-md transition-colors flex items-center justify-between group ${
                    location.pathname === productPaths.xenoarc
                      ? "bg-[#F3F4F7] font-semibold"
                      : "hover:bg-[#F3F4F7] text-[#2D224E]"
                  }`}
                  onClick={() => setIsProductDropdownOpen(false)}
                >
                  Xenoarc
                  <ArrowRightIcon className="w-[16px] h-[16px] hidden group-hover:block" />
                </Link>

                {/* GRID Bot */}
                <Link
                  to={productPaths.grid}
                  className={`w-full text-left text-sm px-3 py-2 rounded-md transition-colors flex items-center justify-between group ${
                    location.pathname === productPaths.grid
                      ? "bg-[#F3F4F7] font-semibold"
                      : "hover:bg-[#F3F4F7] text-[#2D224E]"
                  }`}
                  onClick={() => setIsProductDropdownOpen(false)}
                >
                  GRID Bot
                  <ArrowRightIcon className="w-[16px] h-[16px] hidden group-hover:block" />
                </Link>

                {/* DCA Bot */}
                <Link
                  to={productPaths.dca}
                  className={`w-full text-left text-sm px-3 py-2 rounded-md transition-colors flex flex-col items-start justify-between group ${
                    location.pathname === productPaths.dca
                      ? "bg-[#F3F4F7] font-semibold"
                      : "hover:bg-[#F3F4F7] text-[#2D224E]"
                  }`}
                  onClick={() => setIsProductDropdownOpen(false)}
                  // onClick={(e) => {
                  //   e.preventDefault();
                  //   setIsProductDropdownOpen(false);
                  // }}
                >
                  DCA Bot
                  {/* <span className="text-[11px] bg-[#FFEDD1]/50 text-[#B56F00] px-2 py-1 rounded-lg font-semibold mt-1">
                    Coming soon!
                  </span> */}
                </Link>

                {/* Rebalance Bot (Coming Soon) */}
                <Link
                  className="flex-col w-full text-left text-sm px-3 py-2 hover:bg-[#F3F4F7] rounded-md text-[#2D224E] flex items-start justify-between"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsProductDropdownOpen(false);
                  }}
                >
                  Rebalance Bot
                  <span className="text-[11px] bg-[#FFEDD1]/50 text-[#B56F00] px-2 py-1 rounded-lg font-semibold mt-1">
                    Coming soon!
                  </span>
                </Link>
              </div>
            </>
          )}
        </div>

        {/*  Desktop Digest with active style */}
        <button
          className={`${styles.menuItem} ${
            isActive("/digest") ? styles.menuItemActive : ""
          }`}
          onClick={() => navigate("/digest")}
        >
          Digest
        </button>
        <button
          className={`${styles.menuItem} ${
            isActive("/pricing") ? styles.menuItemActive : ""
          }`}
          onClick={() => {
            if (!!user) {
              navigate("/account?tab=subscription");
            } else {
              navigate("/pricing");
            }
          }}
        >
          Pricing
        </button>
        <button
          className={`${styles.menuItem} ${
            isActive("/events") ? styles.menuItemActive : ""
          }`}
          onClick={() => {
            if (!!user) {
              navigate("/events");
            } else {
              navigate("/events");
            }
          }}
        >
          Events
        </button>
      </div>

      {/* Desktop Buttons */}
      <div
        className={`${styles.buttonsGroup} max-md:hidden gap-4 flex items-center`}
      >
        <button
          className="font-bold text-[#2D224E] text-sm !bg-transparent"
          onClick={() => navigate("/login")}
        >
          Login
        </button>
        <button
          className="font-bold uppercase text-white bg-[#5E48A3] rounded-xl px-6 py-3 text-sm"
          onClick={() => navigate("/signup")}
        >
          Try for 30 days
        </button>
      </div>

      {/* 3. MOBILE MENU */}
      <div
        className={`${styles.mobileMenuOverlay} ${styles.mobileOnly} ${
          isMenuOpen ? styles.open : ""
        }`}
      >
        <div className={styles.mobileMenuContent}>
          {/* A. About Product Section */}
          <div className="w-full ">
            <div
              className={`${styles.mobileMainLink} ${
                isProductGroupActive
                  ? styles.mobileMainLinkActive + " mb-3"
                  : ""
              }`}
              onClick={() => setIsMobileProductOpen(!isMobileProductOpen)}
            >
              About Product
              <ChevronDownIcon
                className={`w-5 h-5 transition-transform ${
                  isMobileProductOpen ? "rotate-180" : ""
                }`}
              />
            </div>

            {isMobileProductOpen && (
              <div className={styles.mobileLinkList}>
                {/* Xenoarc */}
                <button
                  className={`${styles.mobileSubLink} ${
                    location.pathname === productPaths.xenoarc
                      ? styles.mobileSubLinkActive
                      : ""
                  }`}
                  onClick={() => handleMobileLinkClick(productPaths.xenoarc)}
                >
                  Xenoarc
                </button>

                {/* GRID Bot */}
                <button
                  className={`${styles.mobileSubLink} ${
                    location.pathname === productPaths.grid
                      ? styles.mobileSubLinkActive
                      : ""
                  }`}
                  onClick={() => handleMobileLinkClick(productPaths.grid)}
                >
                  GRID Bot
                </button>
                <button
                  className={`${styles.mobileSubLink} ${
                    location.pathname === productPaths.dca
                      ? styles.mobileSubLinkActive
                      : ""
                  }`}
                  onClick={() => handleMobileLinkClick(productPaths.dca)}
                >
                  DCA Bot
                </button>

                {/* DCA Bot */}
                {/* <div className={styles.mobileSubLink}>
                  <span>DCA Bot</span>
                  <span className={styles.comingSoonBadge}>Coming soon!</span>
                </div> */}
                <div className={styles.mobileSubLink}>
                  <span>Rebalance Bot</span>
                  <span className={styles.comingSoonBadge}>Coming soon!</span>
                </div>
              </div>
            )}

            {/* B. Other Links */}
            <div className="flex flex-col items-start w-full">
              <button
                className={`${styles.mobileMainLink} ${
                  location.pathname.startsWith("/digest")
                    ? styles.mobileMainLinkActive
                    : ""
                }`}
                onClick={() => handleMobileLinkClick("/digest")}
              >
                Digest
              </button>
              <button
                className={`${styles.mobileMainLink} ${
                  location.pathname.startsWith("/digest")
                    ? styles.mobileMainLinkActive
                    : ""
                }`}
                onClick={() => {
                  if (!!user) {
                    handleMobileLinkClick("/account?tab=subscription");
                  } else {
                    handleMobileLinkClick("/pricing");
                  }
                }}
              >
                Pricing
              </button>
              <button
                className={`${styles.mobileMainLink} ${
                  location.pathname.startsWith("/events")
                    ? styles.mobileMainLinkActive
                    : ""
                }`}
                onClick={() => handleMobileLinkClick("/events")}
              >
                Events
              </button>
            </div>
          </div>

          {/* C. Bottom Auth */}
          <div className={styles.mobileAuthSection}>
            <button
              className="font-bold text-[#2D224E] w-full text-sm !bg-transparent"
              onClick={() => handleMobileLinkClick("/login")}
            >
              Login
            </button>
            <button
              className="font-bold text-white bg-[#5E48A3] w-full rounded-lg px-6 py-3 text-sm"
              onClick={() => handleMobileLinkClick("/signup")}
            >
              Try 1 Month Free
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};
